import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import "./MfaAppSettingsPage.scss";
import MfaAppService from "../../services/MfaAppService";
import TriggerContent from "./components/TriggerContent";
import FeedbackContent from "./components/FeedbackContent";
import PlatformContent from "./components/PlatformContent";
import AppInfoContent from "./components/AppInfoContent";
import { useSelector, useDispatch } from "react-redux";
import { RootState } from "../../store/store";
import {
  CodeRoundedIcon,
  InfoIcon,
  FeedbackIcon,
  TriggersIcon,
} from "@pl-flight-recorder/web/components/icons";
import {
  setSelectedProject,
  clearSelectedProject,
  selectActiveSettingsContent,
  setActiveSettingsContent,
} from "../../store/slices/onboardingSlice";
import Breadcrumbs, { BreadcrumbItem } from "../../components/Breadcrumbs";

type ContentType = "appInfo" | "triggers" | "feedback" | "sdk";

const MfaAppSettingsPage: React.FC = () => {
  const { projectId } = useParams<{ projectId: string }>();
  const dispatch = useDispatch();
  const [projectName, setProjectName] = useState("");

  // Get the active content from Redux store
  const reduxActiveContent = useSelector(selectActiveSettingsContent);

  // Local state that syncs with Redux
  const [activeContent, setActiveContent] =
    useState<ContentType>(reduxActiveContent);

  const { user } = useSelector((state: RootState) => state.user);

  // Sync local state with Redux when reduxActiveContent changes
  useEffect(() => {
    setActiveContent(reduxActiveContent);
  }, [reduxActiveContent]);

  // Update Redux when local state changes
  const handleContentChange = (content: ContentType) => {
    setActiveContent(content);
    dispatch(setActiveSettingsContent(content));
  };

  useEffect(() => {
    const fetchProject = async () => {
      if (!projectId) return;
      try {
        const mfaAppService = MfaAppService.getInstance();
        const fetchedProject = await mfaAppService.getProject(
          projectId,
          user?.id.toString() || "",
        );
        setProjectName(fetchedProject.project.projectName);
        console.log("Open SDK Capture Settings", projectName);
      } catch (err) {
        console.error("Failed to fetch project:", err);
      }
    };
    fetchProject();
  }, [projectId, user]);

  // Set the selected project ID for onboarding menu visibility
  useEffect(() => {
    if (projectId) {
      dispatch(setSelectedProject(projectId));
    }

    return () => {
      dispatch(clearSelectedProject());
    };
  }, [projectId, dispatch]);

  // Add event listener for setting active content
  useEffect(() => {
    const handleSetActiveContent = (event: CustomEvent) => {
      console.log("Received event with detail:", event.detail);
      console.log("Current activeContent before update:", activeContent);

      if (event.detail === "feedback") {
        console.log("Setting activeContent to 'feedback'");
        handleContentChange("feedback");
      } else if (event.detail === "triggers") {
        console.log("Setting activeContent to 'triggers'");
        handleContentChange("triggers");
      } else if (event.detail === "sdk") {
        console.log("Setting activeContent to 'sdk'");
        handleContentChange("sdk");
      } else if (event.detail === "appInfo") {
        console.log("Setting activeContent to 'appInfo'");
        handleContentChange("appInfo");
      }
    };

    console.log("Adding event listener for setActiveContent");
    window.addEventListener(
      "setActiveContent",
      handleSetActiveContent as EventListener,
    );

    return () => {
      console.log("Removing event listener for setActiveContent");
      window.removeEventListener(
        "setActiveContent",
        handleSetActiveContent as EventListener,
      );
    };
  }, []); // Empty dependency array to avoid recreating the event listener

  const renderContent = () => {
    switch (activeContent) {
      case "appInfo":
        return <AppInfoContent projectId={projectId || ""} />;
      case "triggers":
        return <TriggerContent />;
      case "feedback":
        return <FeedbackContent />;
      case "sdk":
        return <PlatformContent />;
      default:
        return <TriggerContent />;
    }
  };

  const breadcrumbItems: BreadcrumbItem[] = [
    { label: "Apps", path: "/" },
    { label: projectName || "App", path: `/project/${projectId}` },
    { label: "Settings", path: `/project/${projectId}/settings` },
  ];

  return (
    <div className="sdk-capture-settings-page">
      <div className="header-row">
        <div className="left-section">
          <Breadcrumbs items={breadcrumbItems} />
        </div>
      </div>

      <div className="settings-container">
        <div className="settings-sidebar">
          <div className="section">
            <div className="label-m text-secondary">DETAILS</div>
            <div
              className={`sidebar-item ${activeContent === "appInfo" ? "active" : ""}`}
              onClick={() => handleContentChange("appInfo")}
            >
              <span className="icon">
                <InfoIcon />
              </span>
              <div className="item-content">
                <div className="title">App Info</div>
                <div className="description">
                  Customize your app name, objective, and icon
                </div>
              </div>
            </div>
          </div>

          <div className="section">
            <div className="label-m text-secondary">EXPERIENCE</div>
            <div
              className={`sidebar-item ${activeContent === "triggers" ? "active" : ""}`}
              onClick={() => handleContentChange("triggers")}
            >
              <span className="icon">
                <TriggersIcon />
              </span>
              <div className="item-content">
                <div className="title">Triggers</div>
                <div className="description">
                  Determine which actions users will take to provide feedback
                </div>
              </div>
            </div>

            <div
              className={`sidebar-item ${activeContent === "feedback" ? "active" : ""}`}
              onClick={() => handleContentChange("feedback")}
            >
              <span className="icon">
                <FeedbackIcon />
              </span>
              <div className="item-content">
                <div className="title">Feedback Forms</div>
                <div className="description">
                  Build custom forms to collect feedback from users
                </div>
              </div>
            </div>
          </div>

          <div className="section">
            <div className="label-m text-secondary">
              TECHNICAL SPECIFICATIONS
            </div>
            <div
              className={`sidebar-item ${activeContent === "sdk" ? "active" : ""}`}
              onClick={() => handleContentChange("sdk")}
            >
              <span className="icon">
                <CodeRoundedIcon />
              </span>
              <div className="item-content">
                <div className="title">Platforms</div>
                <div className="description">
                  Configure new platforms and check the status of existing ones
                </div>
              </div>
            </div>
          </div>
        </div>

        {renderContent()}
      </div>
    </div>
  );
};

export default MfaAppSettingsPage;
